/**
 * systemTreeClient — the tree shaping the Systems screen needs, mirroring server/utils/systemTree.js.
 *
 * WHY A SECOND COPY AT ALL. The server owns the rules that decide access and responsibility;
 * this owns only what a form needs to stop someone choosing an impossible parent. The server
 * still refuses a cycle (PATCH answers 409) — this exists so the option is greyed out rather
 * than offered and then rejected. If these two ever disagree the server wins, which is why
 * nothing here is used to decide anything but what a dropdown shows.
 */

export interface SystemRow {
  id: number;
  parent_id: number | null;
  name: string;
  source?: 'derived' | 'authored';
  asset_count?: number;
}

export interface SystemNode extends SystemRow {
  children: SystemNode[];
  /** Assets attached AT or BELOW this node — what a parent row shows. */
  rollupCount: number;
  depth: number;
}

/** Nested, roots first, siblings by name, with counts rolled up. */
export const toTree = (rows: SystemRow[]): SystemNode[] => {
  const nodes = new Map<number, SystemNode>();
  for (const row of rows || []) {
    nodes.set(row.id, { ...row, children: [], rollupCount: row.asset_count || 0, depth: 0 });
  }
  const roots: SystemNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parent_id === null ? null : nodes.get(node.parent_id);
    // A node whose parent is missing is shown as a root rather than hidden — losing a system
    // because its parent row vanished is worse than showing it at the top.
    if (parent && parent.id !== node.id) parent.children.push(node);
    else roots.push(node);
  }

  const settle = (list: SystemNode[], depth: number, seen: Set<number>): number => {
    list.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    let total = 0;
    for (const node of list) {
      // Cycle-safe: data that predates the server's guard must not hang the screen.
      if (seen.has(node.id)) { node.children = []; continue; }
      seen.add(node.id);
      node.depth = depth;
      node.rollupCount = (node.asset_count || 0) + settle(node.children, depth + 1, seen);
      total += node.rollupCount;
    }
    return total;
  };
  settle(roots, 0, new Set());
  return roots;
};

/** Depth-first, so a table can render one row per system with indentation. */
export const flatten = (nodes: SystemNode[]): SystemNode[] =>
  nodes.flatMap((node) => [node, ...flatten(node.children)]);

/** The node and everything below it — the ids a parent picker must not offer. */
export const subtreeIds = (rows: SystemRow[], id: number): Set<number> => {
  const childrenOf = new Map<number, number[]>();
  for (const row of rows || []) {
    if (row.parent_id === null) continue;
    if (!childrenOf.has(row.parent_id)) childrenOf.set(row.parent_id, []);
    childrenOf.get(row.parent_id)!.push(row.id);
  }
  const out = new Set<number>();
  const queue = [id];
  while (queue.length) {
    const current = queue.shift()!;
    if (out.has(current)) continue;
    out.add(current);
    for (const child of childrenOf.get(current) || []) queue.push(child);
  }
  return out;
};

/**
 * The systems `id` may legally be moved under: everything except itself and its own
 * descendants. Returned in tree order with depth, so the dropdown can show the shape.
 */
export const parentOptions = (rows: SystemRow[], id: number | null): SystemNode[] => {
  const forbidden = id === null ? new Set<number>() : subtreeIds(rows, id);
  return flatten(toTree(rows)).filter((node) => !forbidden.has(node.id));
};
